/**
 * Firestore queries + doc-shape parsers for the admin UI.
 *
 * Pure read-side helpers — no writes. Two collections:
 *   - `_practitionerRole`: every role assignment in the system
 *   - `_auditEvent`: chronological lifecycle log of role mutations
 *
 * Person display names are resolved via `loadPersonSummaries()` /
 * `loadPersonNames()`, which batch the `_person` reads as parallel
 * `documentId() in [...]` queries of 30 (deduped by doc id within one call).
 * Scale as of 2026-09: ~545 `_practitionerRole` docs (OneTouch-synced carers
 * plus staff), so the panel is one full sweep of roles + ~19 batched person
 * queries; beyond a few thousand it would want pagination + virtualisation.
 */
import {
  collection,
  doc,
  documentId,
  DocumentReference,
  type DocumentSnapshot,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";
import { chunk, FIRESTORE_IN_LIMIT } from "../utils/chunk";
import type { AccessRole, DataScope } from "./claims";
import { formatPersonName } from "./person-name";
import { parseRecorded } from "./recorded";
import { computeRoleStatus, type PractitionerRoleStatus } from "./role-status";
import { personEmail } from "./user-catalogue";

// Mirrors the constants in agent.fhir/practitioner_role.py and
// agent.fhir/audit_event.py. Single definition site per module — if
// either system URI changes, both code paths need updating together.
export const STC_ACCESS_ROLE_SYSTEM =
  "https://sweettreeconnect.com/fhir/CodeSystem/access-role";
export const STC_AUDIT_EVENT_CODE_SYSTEM =
  "https://sweettreeconnect.com/fhir/CodeSystem/audit-event-code";

// ── _practitionerRole ────────────────────────────────────────────────

/**
 * View-model shape rendered by users-roles.ts. Flattens the canonical
 * doc into the fields the UI needs, with display names resolved from
 * `_person`. Keeps the render layer ignorant of FHIR shape so we can
 * change either side independently.
 */
export interface PractitionerRoleRow {
  /** _practitionerRole doc id */
  roleId: string;
  /** _person doc id this role authorises */
  personDocId: string | null;
  /** Display name resolved from _person; null when name lookup failed */
  personName: string | null;
  /** Access role code (carer/planner/...). Null if the doc is malformed. */
  role: AccessRole | null;
  /** dataScope from _practitionerRole._dataScope. Null if absent. */
  dataScope: DataScope | null;
  /** ISO 8601 datetime; null if period absent */
  periodStart: string | null;
  /** ISO 8601 datetime; null for open-ended grants */
  periodEnd: string | null;
  /** True when active=false OR period.end is in the past */
  inactive: boolean;
  /** Raw active flag for diagnostics */
  active: boolean;
  /**
   * OMON-151: three-way operational status discriminator
   * ("active" / "suspended" / "ended"). Derived from `active` + `periodEnd`
   * by `computeRoleStatus()`. Used by users-roles.ts to render distinct
   * status badges and by edit-practitioner-role.ts to decide which
   * suspend/resume button to show. `inactive` is kept for backward compat
   * with code paths that only need the binary in-effect/not-in-effect
   * signal.
   */
  status: PractitionerRoleStatus;
}

function extractRole(roleDoc: Record<string, unknown>): AccessRole | null {
  const codeEntries = roleDoc.code;
  if (!Array.isArray(codeEntries)) return null;
  for (const entry of codeEntries) {
    if (!entry || typeof entry !== "object") continue;
    const coding = (entry as { coding?: unknown }).coding;
    if (!Array.isArray(coding)) continue;
    for (const c of coding) {
      if (!c || typeof c !== "object") continue;
      const cc = c as { system?: unknown; code?: unknown };
      if (cc.system === STC_ACCESS_ROLE_SYSTEM && typeof cc.code === "string") {
        return cc.code as AccessRole;
      }
    }
  }
  return null;
}

function extractDataScope(
  roleDoc: Record<string, unknown>,
): DataScope | null {
  const v = roleDoc._dataScope;
  if (v === "real" || v === "test" || v === "both") return v;
  return null;
}

function extractPersonDocId(roleDoc: Record<string, unknown>): string | null {
  const practitioner = roleDoc.practitioner as
    | { reference?: unknown }
    | undefined;
  const ref = practitioner?.reference;
  if (ref instanceof DocumentReference) return ref.id;
  if (typeof ref === "string" && ref.includes("/")) {
    return ref.split("/").pop() || null;
  }
  return null;
}

function extractPeriod(roleDoc: Record<string, unknown>): {
  start: string | null;
  end: string | null;
} {
  const p = roleDoc.period as { start?: unknown; end?: unknown } | undefined;
  return {
    start: typeof p?.start === "string" ? p.start : null,
    end: typeof p?.end === "string" ? p.end : null,
  };
}

function isInactive(
  roleDoc: Record<string, unknown>,
  end: string | null,
): boolean {
  if (roleDoc.active === false) return true;
  if (end !== null) {
    const endTime = Date.parse(end);
    if (!Number.isNaN(endTime) && endTime < Date.now()) return true;
  }
  return false;
}

export async function loadPractitionerRoles(): Promise<PractitionerRoleRow[]> {
  const snap = await getDocs(collection(db, "_practitionerRole"));
  const rows: PractitionerRoleRow[] = [];
  snap.forEach((d) => {
    const data = d.data();
    const role = extractRole(data);
    const dataScope = extractDataScope(data);
    const personDocId = extractPersonDocId(data);
    const { start, end } = extractPeriod(data);
    const active = data.active !== false;
    rows.push({
      roleId: d.id,
      personDocId,
      personName: null, // filled in by loadPersonNames()
      role,
      dataScope,
      periodStart: start,
      periodEnd: end,
      inactive: isInactive(data, end),
      active,
      status: computeRoleStatus(active, end),
    });
  });
  return rows;
}

// ── _auditEvent ──────────────────────────────────────────────────────

export interface AuditEventRow {
  /** _auditEvent doc id */
  eventId: string;
  /** Recorded instant (ISO 8601). Used for ordering + display. */
  recorded: string | null;
  /** FHIR action code (C/R/U/D/E). Null if malformed. */
  action: string | null;
  /** STC code-system code (role-create / -update / -delete / -period-end) */
  code: string | null;
  /** Human display from the code coding. Null if absent. */
  codeDisplay: string | null;
  /** _person doc id of the agent who initiated the event */
  agentPersonDocId: string | null;
  /** Display name resolved from _person; null until loadPersonNames runs */
  agentName: string | null;
  /** _practitionerRole doc id the event targets, when present */
  entityRoleDocId: string | null;
  /** "success" | "failure" */
  outcome: "success" | "failure" | "unknown";
  /** Optional human-readable outcome detail (failure message etc.) */
  outcomeText: string | null;
  /** Flat list of {field, before, after} field-changes (UPDATE events). */
  changes: Array<{ field: string; before: string; after: string }>;
}

function extractAction(eventDoc: Record<string, unknown>): string | null {
  const a = eventDoc.action;
  if (typeof a === "string" && /^[CRUDE]$/.test(a)) return a;
  return null;
}

function extractCode(eventDoc: Record<string, unknown>): {
  code: string | null;
  display: string | null;
} {
  const codeField = eventDoc.code as { coding?: unknown } | undefined;
  const coding = codeField?.coding;
  if (!Array.isArray(coding)) return { code: null, display: null };
  for (const c of coding) {
    if (!c || typeof c !== "object") continue;
    const cc = c as { system?: unknown; code?: unknown; display?: unknown };
    if (cc.system === STC_AUDIT_EVENT_CODE_SYSTEM && typeof cc.code === "string") {
      return {
        code: cc.code,
        display: typeof cc.display === "string" ? cc.display : null,
      };
    }
  }
  return { code: null, display: null };
}

function extractAgentPersonDocId(
  eventDoc: Record<string, unknown>,
): string | null {
  const agent = eventDoc.agent;
  if (!Array.isArray(agent) || agent.length === 0) return null;
  const first = agent[0] as { who?: { reference?: unknown } } | undefined;
  const ref = first?.who?.reference;
  if (ref instanceof DocumentReference) return ref.id;
  if (typeof ref === "string" && ref.includes("/")) {
    return ref.split("/").pop() || null;
  }
  return null;
}

function extractEntityRoleDocId(
  eventDoc: Record<string, unknown>,
): string | null {
  const entity = eventDoc.entity;
  if (!Array.isArray(entity) || entity.length === 0) return null;
  const first = entity[0] as { what?: { reference?: unknown } } | undefined;
  const ref = first?.what?.reference;
  if (ref instanceof DocumentReference) return ref.id;
  if (typeof ref === "string" && ref.includes("/")) {
    return ref.split("/").pop() || null;
  }
  return null;
}

function extractOutcome(eventDoc: Record<string, unknown>): {
  outcome: "success" | "failure" | "unknown";
  text: string | null;
} {
  const outcome = eventDoc.outcome as
    | {
        code?: { code?: unknown };
        detail?: Array<{ text?: unknown }>;
      }
    | undefined;
  const code = outcome?.code?.code;
  let kind: "success" | "failure" | "unknown" = "unknown";
  if (code === "0") kind = "success";
  else if (typeof code === "string" && code !== "0") kind = "failure";
  const text = outcome?.detail?.[0]?.text;
  return {
    outcome: kind,
    text: typeof text === "string" ? text : null,
  };
}

/**
 * AuditEvent change-detail records are stored as flat triples per
 * FieldChange in audit_event.py — three consecutive entries with codes
 * `field-name`, `before`, `after`. This walks the detail[] array three
 * at a time, treating broken triples as gracefully as possible.
 */
function extractChanges(
  eventDoc: Record<string, unknown>,
): Array<{ field: string; before: string; after: string }> {
  const entity = eventDoc.entity;
  if (!Array.isArray(entity) || entity.length === 0) return [];
  const first = entity[0] as { detail?: unknown } | undefined;
  const detail = first?.detail;
  if (!Array.isArray(detail)) return [];

  const changes: Array<{ field: string; before: string; after: string }> = [];
  let pending: { field?: string; before?: string; after?: string } = {};
  for (const item of detail) {
    if (!item || typeof item !== "object") continue;
    const d = item as {
      type?: { coding?: Array<{ code?: unknown }> };
      valueString?: unknown;
    };
    const codeRaw = d.type?.coding?.[0]?.code;
    const code = typeof codeRaw === "string" ? codeRaw : null;
    const value = typeof d.valueString === "string" ? d.valueString : "";
    if (code === "field-name") {
      pending = { field: value };
    } else if (code === "before") {
      pending.before = value;
    } else if (code === "after") {
      pending.after = value;
      if (pending.field !== undefined) {
        changes.push({
          field: pending.field,
          before: pending.before ?? "",
          after: pending.after,
        });
      }
      pending = {};
    }
  }
  return changes;
}

export async function loadAuditEvents(
  maxRows = 200,
): Promise<AuditEventRow[]> {
  const q = query(
    collection(db, "_auditEvent"),
    orderBy("recorded", "desc"),
    limit(maxRows),
  );
  const snap = await getDocs(q);
  const rows: AuditEventRow[] = [];
  snap.forEach((d) => {
    const data = d.data();
    const { code, display } = extractCode(data);
    const { outcome, text } = extractOutcome(data);
    rows.push({
      eventId: d.id,
      recorded: parseRecorded(data.recorded),
      action: extractAction(data),
      code,
      codeDisplay: display,
      agentPersonDocId: extractAgentPersonDocId(data),
      agentName: null,
      entityRoleDocId: extractEntityRoleDocId(data),
      outcome,
      outcomeText: text,
      changes: extractChanges(data),
    });
  });
  return rows;
}

// ── _person display-name resolution ──────────────────────────────────

// formatPersonName extracted to ./person-name.ts as a pure module so
// tests can import it without pulling in the Firestore client init
// (which depends on esbuild build-time constants). Re-exported here so
// existing callers (users-roles.ts, audit-log.ts) don't have to change
// their imports.
export { formatPersonName };

/**
 * Batch-resolve display names for a set of _person doc ids.
 *
 * Returns a Map keyed by doc id, with null values for docs that
 * couldn't be loaded (network failure, doesn't exist, permission denied).
 * Caller renders nulls as the bare doc id so the row stays meaningful.
 */
export async function loadPersonNames(
  personDocIds: Iterable<string>,
): Promise<Map<string, string | null>> {
  const summaries = await loadPersonSummaries(personDocIds);
  return new Map(
    Array.from(summaries, ([id, s]) => [id, s.name] as [string, string | null]),
  );
}

// ── ADR-0046: person provenance (read side — the chips) ─────────────

/** The `_provenance` block phase 1 stamped on every `_person`: origin is
 *  the immutable birth fact, stewards is per-domain live authority, and
 *  `diverged` is the sync's flag when OneTouch disagrees about a field
 *  STC stewards. Machine-written; this UI only ever READS it. */
export interface PersonProvenance {
  origin: "onetouch" | "stc" | "guest" | string;
  stewards: Record<string, string>;
  diverged?: { at?: string; fields?: string[]; source?: string };
}

export interface PersonSummary {
  name: string | null;
  provenance: PersonProvenance | null;
  /** ADR-0045 §6: the join key to non-production accounts. */
  email: string | null;
}

const EMPTY_SUMMARY: PersonSummary = { name: null, provenance: null, email: null };

function summarise(snap: DocumentSnapshot): PersonSummary {
  const rawProv = snap.get("_provenance") as unknown;
  const provenance =
    rawProv && typeof rawProv === "object" && "origin" in (rawProv as object)
      ? (rawProv as PersonProvenance)
      : null;
  return {
    name: formatPersonName(snap),
    provenance,
    email: personEmail(snap.data() as Record<string, unknown>),
  };
}

/** The old one-read-per-person path, kept as the fallback for a chunk whose
 *  batched query fails — so one unreadable doc still costs only its own row. */
async function loadPersonSummaryOne(id: string): Promise<PersonSummary> {
  try {
    const snap = await getDoc(doc(db, "_person", id));
    return snap.exists() ? summarise(snap) : EMPTY_SUMMARY;
  } catch {
    return EMPTY_SUMMARY;
  }
}

/** One read per person serves the display name, the provenance chip and the
 *  email — callers that need only names (audit log) go through
 *  loadPersonNames above, which delegates here.
 *
 *  2026-09-24 perf: batched as `documentId() in [...]` queries of 30, all in
 *  parallel — Users & roles used to issue ~545 single getDoc calls. A chunk
 *  whose query fails (e.g. a doc the rules refuse, which fails the whole
 *  query) falls back to per-doc reads for just that chunk, preserving the
 *  "null for what couldn't be loaded" contract. */
export async function loadPersonSummaries(
  personDocIds: Iterable<string>,
): Promise<Map<string, PersonSummary>> {
  const ids = Array.from(new Set(personDocIds));
  const out = new Map<string, PersonSummary>();
  await Promise.all(
    chunk(ids, FIRESTORE_IN_LIMIT).map(async (ids30) => {
      try {
        const snap = await getDocs(
          query(collection(db, "_person"), where(documentId(), "in", ids30)),
        );
        snap.forEach((d) => out.set(d.id, summarise(d)));
        // Missing docs are simply absent from an `in` result.
        for (const id of ids30) if (!out.has(id)) out.set(id, EMPTY_SUMMARY);
      } catch {
        const each = await Promise.all(ids30.map(loadPersonSummaryOne));
        ids30.forEach((id, i) => out.set(id, each[i]));
      }
    }),
  );
  return out;
}

